import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { Link, Navigate, useLocation, useOutletContext, useRouteError } from 'react-router'

import type { BookkeeperAuth } from '@/routes/Bookkeeper/api'
import { AppError } from '@/routes/Bookkeeper/AppError'
import { PAGE_COMPONENTS } from '@/routes/Bookkeeper/pages'

export function BookkeeperNotFound() {
  return (
    <section className="setup-card">
      <h1>Page not found</h1>
      <Link to="/bookkeeper">Go to Bookkeeper</Link>
    </section>
  )
}

/**
 * One workspace page, mounted only while the server still lists it.
 *
 * The sidebar payload is the single answer to which pages a customer may open, so a page dropped
 * from it is unreachable by URL as well as absent from the nav.
 */
export function WorkspacePage({ url }: { url: string }) {
  const auth = useOutletContext<BookkeeperAuth>()
  const allowed = (auth.sidebar?.finance ?? []).some((row) => row.url === url)
  if (!allowed) {
    return <Navigate to="/bookkeeper" replace />
  }
  const Component = PAGE_COMPONENTS[url]
  return <Component />
}

/** Keeps bookmarked routes working, including their query parameters. */
export function LegacyRedirect({ pathname }: { pathname: string }) {
  const location = useLocation()
  return <Navigate to={{ pathname, search: location.search }} replace />
}

/** Bookkeeper's error card for a page that failed to load or render, reported once. */
export function BookkeeperRouteError() {
  const error = useRouteError()
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])
  return <AppError />
}
